import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppEvent, useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { UserLiteDataWithRole } from '../../../../api/endpoints/admin';
import { Icon } from '../../../../concepts/resources/Icon';
import { Colors, Input, Menu, remInPx } from '../../../../ui';
import { Badge } from '../../../../ui/Badge';
import { ButtonToggle } from '../../../../ui/ButtonToggle';
import { Checkbox } from '../../../../ui/Checkbox';
import { LoadingGuard } from '../../../../ui/LoadingGuard';
import { DropMenuButton, MenuButton } from '../../../../ui/MenuButton';
import { PageButtonStrip } from '../../../../ui/PageButtonStrip';
import { Tip } from '../../../../ui/Tip';
import { useResolve } from '../../../Container';
import { AccountModel } from '../../../account/AccountModel';
import { DataTable } from '../../../common/DataTable';
import { Copy, Flexer } from '../../../dashboard/elements/LayoutElements';
import { useOnBulkRemoveUserFromAccount, useOnBulkSetUserRoleForAccount } from '../AdminActions';
import { RoleCell, TextCell, UserCell } from '../common/TableElements';
import { TabProps } from './ManagePeopleView';
import { ApprovalSettings, InviteBlock, PendingInviteCount } from './PeopleInvitesListView';

const ActionsCell = ({ value }: { value: UserLiteDataWithRole }) => {
    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const onBulkSetUserRoleForAccount = useOnBulkSetUserRoleForAccount([value.id], setClosed);
    const onBulkRemoveUserFromAccount = useOnBulkRemoveUserFromAccount([value.id], setClosed);

    return <Flexer flex="row">
        <DropMenuButton icon="deqo://icons/hero/dots-horizontal" open={open} setOpen={setOpen}>
            <Flexer minWidth="10">
                <Menu>
                    <Menu.Item onClick={onBulkSetUserRoleForAccount}>
                        <Menu.ItemText>
                            Change Role
                        </Menu.ItemText>
                    </Menu.Item>
                    <Menu.Item onClick={onBulkRemoveUserFromAccount}>
                        <Menu.ItemText>
                            Remove User
                        </Menu.ItemText>
                    </Menu.Item>
                </Menu>
            </Flexer>
        </DropMenuButton>
    </Flexer>;
};

const columnHelper = createColumnHelper<UserLiteDataWithRole>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(5),
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected()
                }
                onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
            />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.roleName, {
        id: 'roleName',
        size: remInPx(8),
        header: () => <span>Role</span>,
        cell: info => <RoleCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row!, {
        id: 'name',
        size: remInPx(24),
        header: () => <span>Name</span>,
        cell: info => <UserCell value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row.email, {
        id: 'email',
        size: remInPx(18),
        header: () => <span>Email</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>Created</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue()?.toLocaleString()}>
                <Copy size="0.9375" flush>{formatDistanceToNow(info.getValue())} ago</Copy>
            </Tip>;
        }
    }),
    // columnHelper.accessor(row => row.lastSeenAt, {
    //     id: 'lastSeenAt',
    //     size: remInPx(12),
    //     header: () => <span>Last Seen</span>,
    //     cell: info => <TextCell value={info.getValue()?.toLocaleDateString()} />
    // }),
    columnHelper.accessor(row => row.id, {
        id: 'id',
        size: remInPx(12),
        header: () => <span>User ID</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row, {
        id: 'actions',
        size: remInPx(3),
        header: () => <span></span>,
        cell: info => <ActionsCell value={info.getValue()} />,
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const useUsersPagedQuery = (search: string, sort: string, startAt: number) => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const invalidate = useCallback(() => qc.invalidateQueries(['admin.users.list']), [qc]);
    useAppEvent('account:userLeave', invalidate);
    useAppEvent('account:setRole', invalidate);

    return useQuery({
        queryKey: ['admin.users.list', search, sort, startAt],
        queryFn: async () => api.exec(v2.admin.users.list(search, sort, startAt, 50)),
    });
};

const useSelection = <T extends { id: string }>(table: Table<T>) => {
    const { rows } = table.getRowModel();
    return rows.filter(x => x.getIsSelected()).map(x => x.original);
};

const useUserStatsQuery = () => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const invalidate = useCallback(() => qc.invalidateQueries(['admin.users.stats']), [qc]);
    useAppEvent('account:userLeave', invalidate);
    useAppEvent('account:setRole', invalidate);

    return useQuery({
        queryKey: ['admin.users.stats'],
        queryFn: async () => api.exec(v2.admin.users.stats()),
    });
};

const MemberCountView = React.memo<{ total: number }>(({ total }) => {

    const { plan } = useObserver(useResolve(AccountModel));
    const metric = plan.features.quantityMetric;
    const comps = Number(plan.features.quantityExtra ?? 0);
    const limit = (metric === 'workspace' ? plan.quantity * 20 : plan.quantity) + comps;

    return <Flexer>{total} / {limit} members</Flexer>;
});

const MemberLimitView = React.memo<{ total: number }>(({ total }) => {

    const { plan } = useObserver(useResolve(AccountModel));

    const metric = plan.features.quantityMetric;
    const comps = Number(plan.features.quantityExtra ?? 0);
    const limit = (metric === 'workspace' ? plan.quantity * 20 : plan.quantity) + comps;

    if (total <= limit) return null;

    return <Badge size="lg" color={Colors.observe}>
        <Icon size="1.25" src="deqo://icons/hero-mini/exclamation-triangle" />
        <span>You are over your member limit!</span>
    </Badge>;
});

export const UserStatsView = React.memo(() => {

    const { data } = useUserStatsQuery();

    if (!data) return null;

    return <Flexer flex="row" gap="1">
        {!!data && <MemberCountView total={data.nonGuests} />}
        {!!data && <MemberLimitView total={data.nonGuests} />}
    </Flexer>;
});

const BulkActionMenu = ({ selection }: { selection: UserLiteDataWithRole[] }) => {

    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const userIds = useMemo(() => selection.map(x => x.id), [selection]);
    const onBulkRemoveUserFromAccount = useOnBulkRemoveUserFromAccount(userIds, setClosed);
    const onBulkSetUserRoleForAccount = useOnBulkSetUserRoleForAccount(userIds, setClosed);

    const label = !!selection.length
        ? `Bulk Actions (${selection.length})`
        : 'Bulk Actions';

    return <MenuButton disabled={!selection.length} label={label} {...{ open, setOpen }}>
        <Menu>
            <Menu.Section first>
                <Menu.Item onClick={onBulkSetUserRoleForAccount}>
                    <Menu.ItemText>
                        Change Roles
                    </Menu.ItemText>
                </Menu.Item>
                <Menu.Item onClick={onBulkRemoveUserFromAccount}>
                    <Menu.ItemText>
                        Remove From Organization
                    </Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
        </Menu>
    </MenuButton>;
};

export const PeopleMembersListView = ({ viewOptions, view, setView, hasFeature }: TabProps & { hasFeature: boolean }) => {

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('roleName:asc');
    const [startAt, setStartAt] = useState(0);
    const { data, isLoading } = useUsersPagedQuery(search, sort, startAt);

    // Reset startAt on search
    useEffect(() => {
        setStartAt(0);
    }, [search]);

    const items = data?.items ?? [];

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        manualSorting: true,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    setSort(`${sorting[0].id}:${sorting[0].desc ? 'desc' : 'asc'}`);
                }
            }
        },
    });

    useEffect(() => {
        table.toggleAllRowsSelected(false);
    }, [data]);

    const selection = useSelection(table);

    return <>
        <Flexer flex="row" gap="1" align="center" padding="1 0">
            <Flexer shrink>
                <BulkActionMenu selection={selection} />
            </Flexer>
            <InviteBlock />
            {hasFeature && <ApprovalSettings />}
        </Flexer>
        <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
            <Flexer flex="row" gap="1">
                <Flexer width="16">
                    <Input placeholder="Search" value={search} onChange={setSearch} />
                </Flexer>
                {hasFeature && <div style={{ position: 'relative' }}>
                    <ButtonToggle options={viewOptions} selected={view} onChange={setView} />
                    <PendingInviteCount />
                </div>}
            </Flexer>
            <Flexer shrink>
                <PageButtonStrip data={data} onPageChange={setStartAt} />
            </Flexer>
        </Flexer>
        <LoadingGuard loading={isLoading}>
            {data && <DataTable table={table} selectFromFirstCellOnly />}
        </LoadingGuard>
    </>;
};
